/**
 * LP Section Normalizer — Guardrail-Hardened (Phase 2 + 004C)
 *
 * GUARDRAIL: sections[] is the only active source of truth for builder logic.
 * GUARDRAIL: section.id is permanent identity; never derived from array index.
 *
 * Converts legacy flat-key snapshot_json into ordered sections[] array.
 * Preserves existing section IDs. Generates deterministic IDs for new sections.
 */

// TRIAD RULE (MA-LP-ARCH-008): Adding a new type here requires:
//   1. Backend render   — register in lp_section_renderers.py _SECTION_RENDERERS
//   2. Preview render   — add to LPSectionPreview.jsx renderPreviewSection()
//   3. Builder editing  — add to LandingPageBuilder.jsx SECTION_CONFIG
const SECTION_ORDER = [
  "hero",
  "trust_signals",
  "funnel_embed",
  "how_it_works",
  "personal_authority",
  "mid_cta",
  "who_this_is_for",
  "who_this_is_not_for",
  "what_happens_next",
  "what_this_is",
  "what_this_is_not",
  "faq",
  "final_cta",
  "seo",
];

const ARRAY_SECTION_TYPES = new Set(["trust_signals", "faq"]);

/**
 * Check whether a section's data has meaningful content.
 */
export function sectionHasData(data) {
  if (data == null) return false;
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === "object") return Object.keys(data).length > 0;
  if (typeof data === "string") return data.trim().length > 0;
  return false;
}

/**
 * Normalize a snapshot_json into an ordered sections[] array.
 * Only returns sections that have data (sparse).
 *
 * GUARDRAIL: Preserves existing section IDs. Only generates an ID when absent.
 * Generated IDs use `sec_${type}` — deterministic and NOT index-derived.
 *
 * @param {Object} snapshotJson - The snapshot_json from a landing page version
 * @returns {Array<{id: string, type: string, data: any}>} Ordered sections (populated only)
 */
export function normalizeSnapshot(snapshotJson) {
  if (!snapshotJson || typeof snapshotJson !== "object") return [];

  // GUARDRAIL: sections[] is the active source of truth; wins if present
  if (Array.isArray(snapshotJson.sections)) {
    return snapshotJson.sections;
  }

  // Legacy flat keys → convert to sections[]
  const sections = [];
  for (const sectionType of SECTION_ORDER) {
    const data = snapshotJson[sectionType];
    if (!sectionHasData(data)) continue;

    sections.push({
      // GUARDRAIL: section.id is permanent identity; UUID-based for uniqueness safety
      id: crypto.randomUUID(),
      type: sectionType,
      data,
    });
  }

  return sections;
}

/**
 * Produce a full builder sections array.
 *
 * - For legacy flat-key snapshots: returns all SECTION_ORDER types (one per type).
 * - For sections[] format: preserves saved order + multi-instance sections,
 *   then appends empty slots for any SECTION_ORDER types not yet present.
 *
 * GUARDRAIL: section.id is permanent identity; UUID-based for uniqueness safety.
 *
 * @param {Object} snapshotJson - The snapshot_json from a landing page version
 * @returns {Array<{id: string, type: string, data: any}>} Builder-ready sections array
 */
export function toBuilderSections(snapshotJson) {
  if (!snapshotJson || typeof snapshotJson !== "object") {
    return SECTION_ORDER.map((type) => ({
      id: crypto.randomUUID(),
      type,
      data: ARRAY_SECTION_TYPES.has(type) ? [] : {},
    }));
  }

  const normalized = normalizeSnapshot(snapshotJson);
  const hasNewFormat = Array.isArray(snapshotJson.sections);

  if (hasNewFormat) {
    // sections[] format: preserve saved order + multi-instance, add missing types
    const presentTypes = new Set(normalized.map((s) => s.type));
    const result = [...normalized];
    for (const type of SECTION_ORDER) {
      if (!presentTypes.has(type)) {
        result.push({
          id: crypto.randomUUID(),
          type,
          data: ARRAY_SECTION_TYPES.has(type) ? [] : {},
        });
      }
    }
    return result;
  }

  // Legacy flat-key format: fill all SECTION_ORDER slots (one per type)
  const dataMap = {};
  for (const sec of normalized) {
    dataMap[sec.type] = sec;
  }
  return SECTION_ORDER.map((type) => {
    if (dataMap[type]) return dataMap[type];
    return {
      id: crypto.randomUUID(),
      type,
      data: ARRAY_SECTION_TYPES.has(type) ? [] : {},
    };
  });
}

/**
 * Filter a sections array to only populated sections (for clean save path).
 *
 * @param {Array} sections - Full sections array from builder state
 * @returns {Array} Only sections with meaningful data
 */
export function getPopulatedSections(sections) {
  return sections.filter((s) => sectionHasData(s.data));
}

export { SECTION_ORDER };
